import { useState } from "react";
import axios from "axios";
import { Download, RefreshCw } from "lucide-react";
import { API } from "@/constants/site";

export default function AdminList() {
  const [key, setKey] = useState("");
  const [subscribers, setSubscribers] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/subscribers`, { headers: { "X-Admin-Key": key } });
      setSubscribers(data.subscribers);
    } catch (err) {
      setError(err.response?.status === 401 ? "Invalid admin key." : "Could not load the list.");
      setSubscribers(null);
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    setError("");
    try {
      const response = await axios.get(`${API}/subscribers.csv`, {
        headers: { "X-Admin-Key": key },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "umi-speaks-subscribers.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.status === 401 ? "Invalid admin key." : "Download failed.");
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#131110] px-6 py-24 text-[#F2EFE9]">
      <div className="w-full max-w-xl border border-[#D49A36]/20 bg-[#1E1A18] p-8 md:p-12">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#D49A36]">Umi Speaks — Private</p>
        <h1 className="font-display text-3xl font-medium italic md:text-4xl">The List</h1>
        <p className="mt-3 text-sm text-[#DCD8D3]/80">Enter your admin key to view or download every email saved from the site.</p>

        <label className="mt-8 block">
          <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.3em] text-[#DCD8D3]/60">Admin key</span>
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Paste your key"
            className="w-full border border-[#D49A36]/25 bg-[#131110] px-4 py-3 text-sm text-[#F2EFE9] outline-none transition-colors duration-300 placeholder:text-[#DCD8D3]/30 focus:border-[#D49A36]"
          />
        </label>

        <div className="mt-6 flex gap-3">
          <button
            data-testid="admin-load-button"
            onClick={load}
            disabled={!key || loading}
            className="flex items-center gap-2 bg-[#D49A36] px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#131110] transition-colors duration-300 hover:bg-[#F2EFE9] disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4" strokeWidth={2} />
            {loading ? "Loading…" : "Load list"}
          </button>
          <button
            data-testid="admin-download-button"
            onClick={download}
            disabled={!key}
            className="flex items-center gap-2 border border-[#D49A36]/40 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-[#D49A36] transition-colors duration-300 hover:bg-[#D49A36] hover:text-[#131110] disabled:opacity-50"
          >
            <Download className="h-4 w-4" strokeWidth={2} />
            Download CSV
          </button>
        </div>

        {error && (
          <p data-testid="admin-error" className="mt-5 text-sm text-[#C96F5B]">
            {error}
          </p>
        )}

        {subscribers && (
          <div data-testid="admin-list" className="mt-8">
            <p className="mb-3 text-xs uppercase tracking-[0.25em] text-[#DCD8D3]/60">
              {subscribers.length} {subscribers.length === 1 ? "subscriber" : "subscribers"}
            </p>
            <ul className="max-h-72 space-y-2 overflow-y-auto border-t border-[#D49A36]/15 pt-4">
              {subscribers.map((sub) => (
                <li key={sub.email} className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-[#F2EFE9]">{sub.email}</span>
                  <span className="text-xs text-[#DCD8D3]/50">
                    {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </main>
  );
}
